import { Link, useParams } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import ScrollReveal from '../components/ScrollReveal';
import { blogPosts, getPostBySlug } from '../data/blog';
import { useSEO } from '../hooks/useSEO';
import { PAGE_SEO } from '../utils/seo';

const categoryPillColors: Record<string, string> = {
  Engineering: 'pill--green',
  Philosophy: 'pill--blue',
  Design: 'pill--orange',
};

const authorGradients: Record<string, string> = {
  Sidhant: 'linear-gradient(135deg, #1ed760 0%, #0d7a3a 100%)',
  Akanksha: 'linear-gradient(135deg, #a855f7 0%, #6b21a8 100%)',
};

function renderInlineFormatting(text: string): React.ReactNode {
  // Split by bold markers **text** and render each segment
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={idx} style={{ color: 'var(--text-near-white)' }}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={idx} style={{
          background: 'rgba(255,255,255,0.08)',
          padding: '2px 6px',
          borderRadius: '4px',
          fontFamily: 'var(--font-mono, monospace)',
          fontSize: '0.88em',
        }}>
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
}

function renderContent(content: string): React.ReactNode[] {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Skip empty lines
    if (line.trim() === '') {
      i++;
      continue;
    }

    // h2 headings (## )
    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={`h2-${i}`} className="h-3" style={{ marginTop: 'var(--space-8)', marginBottom: 'var(--space-3)', color: 'var(--accent-green)' }}>
          {renderInlineFormatting(line.replace('## ', ''))}
        </h2>
      );
      i++;
      continue;
    }

    // h3 headings (### )
    if (line.startsWith('### ')) {
      elements.push(
        <h3 key={`h3-${i}`} className="h-4" style={{ marginTop: 'var(--space-6)', marginBottom: 'var(--space-2)', color: 'var(--text-warning)' }}>
          {renderInlineFormatting(line.replace('### ', ''))}
        </h3>
      );
      i++;
      continue;
    }

    // Unordered list item (- )
    if (line.trim().startsWith('- ')) {
      const listItems: React.ReactNode[] = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        const itemText = lines[i].trim().replace(/^- /, '');
        listItems.push(
          <li key={`li-${i}`} style={{ marginBottom: 'var(--space-1)', color: 'var(--text-near-white)', lineHeight: 1.7 }}>
            {renderInlineFormatting(itemText)}
          </li>
        );
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} style={{ paddingLeft: 'var(--space-6)', marginBottom: 'var(--space-4)' }}>
          {listItems}
        </ul>
      );
      continue;
    }

    // Regular paragraph text
    if (!line.startsWith('#')) {
      // Collect consecutive non-heading, non-empty, non-list lines into one paragraph
      const paragraphLines: string[] = [];
      while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('#') && !lines[i].trim().startsWith('- ')) {
        paragraphLines.push(lines[i]);
        i++;
      }
      if (paragraphLines.length > 0) {
        const text = paragraphLines.join(' ');
        // Check if it starts with a quote mark
        if (text.startsWith('"')) {
          elements.push(
            <blockquote
              key={`p-${i}`}
              style={{
                borderLeft: '3px solid var(--accent-green)',
                paddingLeft: 'var(--space-4)',
                margin: 'var(--space-4) 0',
                fontStyle: 'italic',
                color: 'var(--text-near-white)',
                lineHeight: 1.7,
              }}
            >
              {renderInlineFormatting(text)}
            </blockquote>
          );
        } else {
          elements.push(
            <p key={`p-${i}`} className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              {renderInlineFormatting(text)}
            </p>
          );
        }
      }
      continue;
    }

    i++;
  }

  return elements;
}

export default function JournalPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  // Apply dynamic SEO for journal post
  useSEO(post ? {
    title: `${post.title} — Journal | Siak Studios`,
    description: post.excerpt,
    canonical: `/journal/${slug}`,
    keywords: [post.category.toLowerCase(), 'journal', 'blog', 'siak studios', 'engineering', 'design philosophy'],
    schema: {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      articleBody: post.content,
      author: {
        '@type': 'Person',
        name: post.author,
      },
      datePublished: post.date,
      dateModified: post.date,
      publisher: {
        '@type': 'Organization',
        name: 'Siak Studios',
        logo: {
          '@type': 'ImageObject',
          url: 'https://siak.in/logo.png'
        }
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://siak.in/journal/${slug}`
      },
      keywords: post.category,
    },
  } : PAGE_SEO.notFound);

  if (!post) {
    return (
      <PageTransition>
        <section className="section" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <h2 className="h-2" style={{ marginBottom: 'var(--space-4)' }}>
              Post not found
            </h2>
            <Link to="/journal" className="btn btn--primary">
              Back to Journal
            </Link>
          </div>
        </section>
      </PageTransition>
    );
  }

  const otherPosts = blogPosts.filter((p) => p.slug !== slug && !p.featured).slice(0, 2);
  const authorGradient = authorGradients[post.author] || 'linear-gradient(135deg, #539df5 0%, #2858a8 100%)';

  return (
    <PageTransition>
      <div className="container">
        {/* Back Link */}
        <ScrollReveal>
          <Link
            to="/journal"
            className="back-link"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--text-secondary)', paddingTop: 'var(--space-8)', fontSize: '0.88rem', fontWeight: 600, marginBottom: 'var(--space-6)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Journal
          </Link>
        </ScrollReveal>

        {/* Post Header */}
        <ScrollReveal>
          <header style={{ maxWidth: '800px', margin: '0 auto var(--space-10)' }}>
            <span className={`pill ${categoryPillColors[post.category] || ''}`} style={{ marginBottom: 'var(--space-4)', display: 'inline-flex' }}>
              {post.category}
            </span>
            <h1 className="h-1" style={{ marginBottom: 'var(--space-4)' }}>
              {post.title}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: authorGradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#fff',
                  }}
                >
                  {post.author[0]}
                </div>
                <span className="caption" style={{ color: 'var(--text-secondary)' }}>
                  {post.author}
                </span>
              </div>
              <span className="caption" style={{ color: 'var(--text-secondary)' }}>
                {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
              <span className="caption" style={{ color: 'var(--text-secondary)' }}>
                {post.readTime}
              </span>
            </div>
          </header>
        </ScrollReveal>

        {/* Post Content */}
        <ScrollReveal>
          <article style={{ maxWidth: '800px', margin: '0 auto', paddingBottom: 'var(--space-10)' }}>
            {renderContent(post.content)}
          </article>
        </ScrollReveal>

        {/* Divider */}
        <div className="divider" style={{ maxWidth: '800px', margin: '0 auto' }} />

        {/* More from the Journal */}
        {otherPosts.length > 0 && (
          <ScrollReveal>
            <section style={{ maxWidth: '800px', margin: '0 auto', paddingTop: 'var(--space-10)', paddingBottom: 'var(--space-12)' }}>
              <h2 className="h-3" style={{ marginBottom: 'var(--space-6)' }}>
                More from the Journal
              </h2>
              <div className="grid-2">
                {otherPosts.map((otherPost) => (
                  <article key={otherPost.id} className="card">
                    <span className={`pill ${categoryPillColors[otherPost.category] || ''}`} style={{ marginBottom: 'var(--space-2)', display: 'inline-flex', fontSize: '0.66rem' }}>
                      {otherPost.category}
                    </span>
                    <h3 className="h-4" style={{ marginBottom: 'var(--space-2)' }}>
                      {otherPost.title}
                    </h3>
                    <p className="caption" style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-3)', lineHeight: 1.5 }}>
                      {otherPost.excerpt}
                    </p>
                    <Link
                      to={`/journal/${otherPost.slug}`}
                      className="btn btn--ghost btn--small"
                      style={{ fontSize: '0.75rem' }}
                    >
                      Read More
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </Link>
                  </article>
                ))}
              </div>
            </section>
          </ScrollReveal>
        )}
      </div>
    </PageTransition>
  );
}
